import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { chatKeys } from '../../keys'
import chatService from '../services'

import type { components } from '@/schema'

type CreateCustomerTagDto = components['schemas']['CreateCustomerTagDto']
type UpdateCustomerTagDto = components['schemas']['UpdateCustomerTagDto']

export function useQueryTags() {
	return useQuery({
		queryKey: chatKeys.tags(),
		queryFn: () => chatService.getTags(),
	})
}

export function useMutationCreateTag() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (data: CreateCustomerTagDto) => chatService.createTag(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: chatKeys.tags() })
			toast.success('สร้างแท็กเรียบร้อยแล้ว')
		},
		onError: () => {
			toast.error('สร้างแท็กไม่สำเร็จ กรุณาลองใหม่อีกครั้ง')
		},
	})
}

export function useMutationUpdateTag() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateCustomerTagDto }) =>
			chatService.updateTag(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: chatKeys.tags() })
			toast.success('แก้ไขแท็กเรียบร้อยแล้ว')
		},
		onError: () => {
			toast.error('แก้ไขแท็กไม่สำเร็จ กรุณาลองใหม่อีกครั้ง')
		},
	})
}

export function useMutationDeleteTag() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (id: string) => chatService.deleteTag(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: chatKeys.tags() })
			toast.success('ลบแท็กเรียบร้อยแล้ว')
		},
		onError: () => {
			toast.error('ลบแท็กไม่สำเร็จ กรุณาลองใหม่อีกครั้ง')
		},
	})
}

export function useMutationAddTagsToRoom() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ roomId, tagIds }: { roomId: string; tagIds: string[] }) =>
			chatService.addTagsToRoom(roomId, { tagIds }),
		onSuccess: (data, { roomId }) => {
			queryClient.setQueryData(chatKeys.room(roomId), data)
			queryClient.invalidateQueries({ queryKey: chatKeys.rooms() })
			toast.success('เพิ่มแท็กเรียบร้อยแล้ว')
		},
		onError: () => {
			toast.error('เพิ่มแท็กไม่สำเร็จ กรุณาลองใหม่อีกครั้ง')
		},
	})
}

export function useMutationRemoveTagFromRoom() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ roomId, tagId }: { roomId: string; tagId: string }) =>
			chatService.removeTagFromRoom(roomId, tagId),
		onSuccess: (data, { roomId }) => {
			queryClient.setQueryData(chatKeys.room(roomId), data)
			queryClient.invalidateQueries({ queryKey: chatKeys.rooms() })
			toast.success('ลบแท็กเรียบร้อยแล้ว')
		},
		onError: () => {
			toast.error('ลบแท็กไม่สำเร็จ กรุณาลองใหม่อีกครั้ง')
		},
	})
}
